<template>
  <div class="side-bar__panel side-bar__panel--menu">
    <input class="hidden-file" id="import-markdown-file-input" type="file" @change="onImportMarkdown">
    <label class="menu-entry button flex flex--row flex--align-center" for="import-markdown-file-input">
      <div class="menu-entry__icon flex flex--column flex--center">
        <icon-file-plus></icon-file-plus>
      </div>
      <div class="menu-entry__text flex flex--column">
        <div>Import Markdown</div>
        <span>Import a plain text file.</span>
      </div>
    </label>
    <input class="hidden-file" id="import-html-file-input" type="file" @change="onImportHtml">
    <label class="menu-entry button flex flex--row flex--align-center" for="import-html-file-input">
      <div class="menu-entry__icon flex flex--column flex--center">
        <icon-code-tags></icon-code-tags>
      </div>
      <div class="menu-entry__text flex flex--column">
        <div>Import HTML</div>
        <span>Convert an HTML file to Markdown.</span>
      </div>
    </label>
    <hr>
    <menu-entry @click="exportMarkdown">
      <template #icon><icon-download></icon-download></template>
      <div>Export as Markdown</div>
      <span>Save plain text file.</span>
    </menu-entry>
    <menu-entry @click="exportHtml">
      <template #icon><icon-printer></icon-printer></template>
      <div>Export as HTML</div>
      <span>Generate an HTML page from a template.</span>
    </menu-entry>
    <menu-entry @click="exportAllMarkdown">
      <template #icon><icon-file-multiple></icon-file-multiple></template>
      <div>Export All as Markdown</div>
      <span>Download all files as a ZIP archive.</span>
    </menu-entry>
    <hr>
    <menu-entry @click="exportBackup">
      <template #icon><icon-content-save></icon-content-save></template>
      <div>Export Workspace Backup</div>
      <span>Save all files and settings as JSON.</span>
    </menu-entry>
    <input class="hidden-file" id="import-backup-file-input" type="file" accept=".json" @change="onImportBackup">
    <label class="menu-entry button flex flex--row flex--align-center" for="import-backup-file-input">
      <div class="menu-entry__icon flex flex--column flex--center">
        <icon-upload></icon-upload>
      </div>
      <div class="menu-entry__text flex flex--column">
        <div>Import Workspace Backup</div>
        <span>Restore from a backup file.</span>
      </div>
    </label>
  </div>
</template>

<script>
import TurndownService from 'turndown/lib/turndown.browser.umd';
import JSZip from 'jszip';
import htmlSanitizer from '../../libs/htmlSanitizer';
import MenuEntry from './common/MenuEntry.vue';
import store from '../../store';
import workspaceSvc from '../../services/workspaceSvc';
import exportSvc from '../../services/exportSvc';
import localDbSvc from '../../services/localDbSvc';

const turndownService = new TurndownService(store.getters['data/computedSettings'].turndown);

const readFile = file => new Promise((resolve) => {
  if (file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      if (content.match(/\uFFFD/)) {
        store.dispatch('notification/error', 'File is not readable.');
      } else {
        resolve(content);
      }
    };
    reader.readAsText(file);
  }
});

// Helper to close sidebar on mobile
const closeSidebarOnMobile = () => {
  if (window.innerWidth <= 900) {
    store.dispatch('data/toggleSideBar', false);
  }
};

export default {
  components: {
    MenuEntry,
  },
  methods: {
    async onImportMarkdown(evt) {
      const file = evt.target.files[0];
      const content = await readFile(file);
      const item = await workspaceSvc.createFile({
        text: content,
        name: file.name,
      });
      store.commit('file/setCurrentId', item.id);
      closeSidebarOnMobile();
    },
    async onImportHtml(evt) {
      const file = evt.target.files[0];
      const content = await readFile(file);
      const sanitizedContent = htmlSanitizer.sanitizeHtml(content)
        .replace(/&#160;/g, ' '); // Replace non-breaking spaces with classic spaces
      const item = await workspaceSvc.createFile({
        text: turndownService.turndown(sanitizedContent),
        name: file.name,
      });
      store.commit('file/setCurrentId', item.id);
      closeSidebarOnMobile();
    },
    async exportMarkdown() {
      const currentFile = store.getters['file/current'];
      try {
        await exportSvc.exportToDisk(currentFile.id, 'md');
        closeSidebarOnMobile();
      } catch (e) { /* Cancel */ }
    },
    async exportHtml() {
      try {
        closeSidebarOnMobile();
        await store.dispatch('modal/open', 'htmlExport');
      } catch (e) { /* Cancel */ }
    },
    async exportBackup() {
      try {
        const backup = await localDbSvc.exportBackup();
        const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `notes-backup-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        store.dispatch('notification/info', 'Backup exported successfully.');
        closeSidebarOnMobile();
      } catch (e) {
        store.dispatch('notification/error', 'Failed to export backup.');
      }
    },
    async onImportBackup(evt) {
      const file = evt.target.files[0];
      if (!file) return;
      
      try {
        const content = await file.text();
        const backup = JSON.parse(content);
        await localDbSvc.importBackup(backup);
        store.dispatch('notification/info', 'Backup imported successfully. Reloading...');
        setTimeout(() => window.location.reload(), 1500);
      } catch (e) {
        store.dispatch('notification/error', 'Failed to import backup. Invalid file format.');
      }
      
      // Reset file input
      evt.target.value = '';
    },
    async exportAllMarkdown() {
      try {
        const zip = new JSZip();
        const files = store.getters['file/items'];
        const folders = store.getters['folder/items'];
        
        // Build folder path map
        const folderPaths = {};
        const buildFolderPath = (folderId) => {
          if (!folderId || folderId === 'trash') return '';
          if (folderPaths[folderId]) return folderPaths[folderId];
          
          const folder = folders.find(f => f.id === folderId);
          if (!folder) return '';
          
          const parentPath = buildFolderPath(folder.parentId);
          const path = parentPath ? `${parentPath}/${folder.name}` : folder.name;
          folderPaths[folderId] = path;
          return path;
        };
        
        // Process each file (excluding trash)
        let fileCount = 0;
        for (const file of files) {
          if (file.parentId === 'trash') continue;
          
          // Load content
          try {
            await localDbSvc.loadItem(`${file.id}/content`);
          } catch (e) {
            continue; // Skip files without content
          }
          
          const content = store.state.content.itemsById[`${file.id}/content`];
          if (!content || !content.text) continue;
          
          // Build file path
          const folderPath = buildFolderPath(file.parentId);
          const fileName = file.name.endsWith('.md') ? file.name : `${file.name}.md`;
          const filePath = folderPath ? `${folderPath}/${fileName}` : fileName;
          
          zip.file(filePath, content.text);
          fileCount += 1;
        }
        
        if (fileCount === 0) {
          store.dispatch('notification/error', 'No files to export.');
          return;
        }
        
        // Generate and download zip
        const blob = await zip.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `notes-export-${new Date().toISOString().split('T')[0]}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        store.dispatch('notification/info', `Exported ${fileCount} files.`);
        closeSidebarOnMobile();
      } catch (e) {
        store.dispatch('notification/error', 'Failed to export files.');
      }
    },
  },
};
</script>
